import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, Search, Filter, Plus, Edit, Trash2, Eye, Download, Upload,
  MapPin, Phone, Mail, RefreshCw, ChevronLeft, ChevronRight, X,
  BarChart2, TrendingUp, Building, Globe, FileText, Check, AlertCircle, Map
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import SpainHeatMap from '../../components/SpainHeatMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMUNIDADES = [
  { id: 'andalucia', name: 'Andalucía' },
  { id: 'aragon', name: 'Aragón' },
  { id: 'asturias', name: 'Asturias' },
  { id: 'baleares', name: 'Islas Baleares' },
  { id: 'canarias', name: 'Canarias' },
  { id: 'cantabria', name: 'Cantabria' },
  { id: 'castilla_leon', name: 'Castilla y León' },
  { id: 'castilla_mancha', name: 'Castilla-La Mancha' },
  { id: 'cataluna', name: 'Cataluña' },
  { id: 'valencia', name: 'Comunidad Valenciana' },
  { id: 'extremadura', name: 'Extremadura' },
  { id: 'galicia', name: 'Galicia' },
  { id: 'madrid', name: 'Comunidad de Madrid' },
  { id: 'murcia', name: 'Región de Murcia' },
  { id: 'navarra', name: 'Navarra' },
  { id: 'pais_vasco', name: 'País Vasco' },
  { id: 'rioja', name: 'La Rioja' },
  { id: 'ceuta', name: 'Ceuta' },
  { id: 'melilla', name: 'Melilla' }
];

function AdminClients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  
  // Filters
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [comunidadFilter, setComunidadFilter] = useState('');
  
  // Analytics
  const [analytics, setAnalytics] = useState({});
  const [byCity, setByCity] = useState([]);
  const [byProvince, setByProvince] = useState([]);
  const [byComunidad, setByComunidad] = useState([]);
  const [trend, setTrend] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [devicePrefs, setDevicePrefs] = useState([]);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [importResult, setImportResult] = useState(null);
  
  // Form
  const [formData, setFormData] = useState({
    email: '', name: '', phone: '', address: '', street_name: '', street_number: '',
    city: '', postal_code: '', province: '', comunidad: '', country: 'España', notes: '', tags: ''
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchClients();
    fetchAnalytics();
  }, [user, navigate, currentPage]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...(search && { search }),
        ...(cityFilter && { city: cityFilter }),
        ...(provinceFilter && { province: provinceFilter }),
        ...(comunidadFilter && { comunidad: comunidadFilter })
      });
      
      const res = await fetch(`${API_URL}/api/clients?${params}`);
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients);
        setTotalClients(data.total);
        setTotalPages(data.pages);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
    setLoading(false);
  }, [currentPage, search, cityFilter, provinceFilter, comunidadFilter]);

  const fetchAnalytics = async () => {
    try {
      const [summaryRes, cityRes, provinceRes, comunidadRes, trendRes, topRes, deviceRes] = await Promise.all([
        fetch(`${API_URL}/api/clients/analytics/summary`),
        fetch(`${API_URL}/api/clients/analytics/by-city?limit=10`),
        fetch(`${API_URL}/api/clients/analytics/by-province`),
        fetch(`${API_URL}/api/clients/analytics/by-comunidad`),
        fetch(`${API_URL}/api/clients/analytics/registration-trend?days=30`),
        fetch(`${API_URL}/api/clients/analytics/top-clients?limit=5`),
        fetch(`${API_URL}/api/clients/analytics/device-preferences`)
      ]);
      
      if (summaryRes.ok) setAnalytics(await summaryRes.json());
      if (cityRes.ok) setByCity((await cityRes.json()).by_city || []);
      if (provinceRes.ok) setByProvince((await provinceRes.json()).by_province || []);
      if (comunidadRes.ok) setByComunidad((await comunidadRes.json()).by_comunidad || []);
      if (trendRes.ok) setTrend((await trendRes.json()).trend || []);
      if (topRes.ok) setTopClients((await topRes.json()).top_clients || []);
      if (deviceRes.ok) setDevicePrefs((await deviceRes.json()).device_preferences || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchClients();
  };

  const clearFilters = () => {
    setSearch('');
    setCityFilter('');
    setProvinceFilter('');
    setComunidadFilter('');
    setCurrentPage(1);
    fetchClients();
  };

  const openCreateModal = () => {
    setFormData({
      email: '', name: '', phone: '', address: '', street_name: '', street_number: '',
      city: '', postal_code: '', province: '', comunidad: '', country: 'España', notes: '', tags: ''
    });
    setShowCreateModal(true);
  };

  const openEditModal = (client) => {
    setSelectedClient(client);
    setFormData({
      email: client.email || '',
      name: client.name || '',
      phone: client.phone || '',
      address: client.address || '',
      street_name: client.street_name || '',
      street_number: client.street_number || '',
      city: client.city || '',
      postal_code: client.postal_code || '',
      province: client.province || '',
      comunidad: client.comunidad || '',
      country: client.country || 'España',
      notes: client.notes || '',
      tags: (client.tags || []).join(', ')
    });
    setShowEditModal(true);
  };

  const openDetailModal = async (client) => {
    try {
      const res = await fetch(`${API_URL}/api/clients/${client.user_id}`);
      if (res.ok) {
        const fullClient = await res.json();
        setSelectedClient(fullClient);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
    }
  };

  const handleCreate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
        })
      });
      
      if (res.ok) {
        setShowCreateModal(false);
        fetchClients();
        fetchAnalytics();
      } else {
        const error = await res.json();
        alert(error.detail || 'Error creating client');
      }
    } catch (error) {
      console.error('Error creating client:', error);
    }
  };

  const handleUpdate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clients/${selectedClient.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
        })
      });
      
      if (res.ok) {
        setShowEditModal(false);
        fetchClients();
      }
    } catch (error) {
      console.error('Error updating client:', error);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`¿Eliminar cliente ${client.name || client.email}?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/clients/${client.user_id}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        fetchClients();
        fetchAnalytics();
      }
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/clients/import`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const result = await res.json();
        setImportResult(result);
        fetchClients();
        fetchAnalytics();
      }
    } catch (error) {
      console.error('Error importing clients:', error);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        ...(cityFilter && { city: cityFilter }),
        ...(provinceFilter && { province: provinceFilter }),
        ...(comunidadFilter && { comunidad: comunidadFilter })
      });
      
      const res = await fetch(`${API_URL}/api/clients/export?${params}`);
      if (res.ok) {
        const data = await res.json();
        
        // Download CSV
        const blob = new Blob([data.csv_content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = data.filename;
        link.click();
      }
    } catch (error) {
      console.error('Error exporting clients:', error);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clients/import/template`);
      if (res.ok) {
        const data = await res.json();
        
        const blob = new Blob([data.template_csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'clients_import_template.csv';
        link.click();
      }
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  if (loading && clients.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-clients">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestión de Clientes</h1>
              <p className="text-gray-600 mt-1">
                {totalClients} clientes registrados
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setShowImportModal(true)} variant="outline" size="sm">
                <Upload className="w-4 h-4 mr-2" />
                Importar
              </Button>
              <Button onClick={handleExport} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
              <Button onClick={openCreateModal}>
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Cliente
              </Button>
              <Button onClick={() => navigate('/admin')} variant="outline">
                Volver al Panel
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Clientes</p>
                  <p className="text-2xl font-bold">{analytics.total_clients || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Nuevos (30d)</p>
                  <p className="text-2xl font-bold text-green-600">{analytics.new_clients_30d || 0}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Con Garantías</p>
                  <p className="text-2xl font-bold">{analytics.clients_with_warranties || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Activos (30d)</p>
                  <p className="text-2xl font-bold">{analytics.active_clients_30d || 0}</p>
                </div>
                <BarChart2 className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="list">
              <Users className="w-4 h-4 mr-2" />
              Lista de Clientes
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart2 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
          </TabsList>

          {/* Client List Tab */}
          <TabsContent value="list">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <Label>Buscar</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Email, nombre, teléfono..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Ciudad</Label>
                    <Input
                      placeholder="Filtrar por ciudad"
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input
                      placeholder="Filtrar por provincia"
                      value={provinceFilter}
                      onChange={(e) => setProvinceFilter(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Comunidad</Label>
                    <Select value={comunidadFilter || "all"} onValueChange={(v) => setComunidadFilter(v === "all" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {COMUNIDADES.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2">
                    <Button onClick={handleSearch} className="flex-1">
                      <Filter className="w-4 h-4 mr-2" />
                      Filtrar
                    </Button>
                    <Button onClick={clearFilters} variant="outline">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Client Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-gray-600">Cliente</th>
                        <th className="text-left p-4 font-medium text-gray-600">Contacto</th>
                        <th className="text-left p-4 font-medium text-gray-600">Ubicación</th>
                        <th className="text-left p-4 font-medium text-gray-600">Garantías</th>
                        <th className="text-left p-4 font-medium text-gray-600">Fecha</th>
                        <th className="text-right p-4 font-medium text-gray-600">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {clients.map((client) => (
                        <tr key={client.user_id} className="hover:bg-gray-50">
                          <td className="p-4">
                            <div className="font-medium">{client.name || '-'}</div>
                            <div className="text-sm text-gray-500">{client.email}</div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="w-3 h-3 text-gray-400" />
                              {client.phone || '-'}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1 text-sm">
                              <MapPin className="w-3 h-3 text-gray-400" />
                              {client.city || '-'}
                              {client.province && `, ${client.province}`}
                            </div>
                          </td>
                          <td className="p-4">
                            <Badge variant={client.warranty_count > 0 ? "default" : "secondary"}>
                              {client.warranty_count || 0}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm text-gray-500">
                            {client.created_at ? new Date(client.created_at).toLocaleDateString('es-ES') : '-'}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openDetailModal(client)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openEditModal(client)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(client)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t">
                    <div className="text-sm text-gray-500">
                      Página {currentPage} de {totalPages} ({totalClients} clientes)
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            {/* Heat Map - Full Width */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Map className="w-5 h-5 text-blue-600" />
                  Mapa de Calor - Distribución de Clientes por Comunidad
                </CardTitle>
                <CardDescription>
                  Haz clic en una comunidad para filtrar clientes de esa zona
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SpainHeatMap 
                  data={byComunidad} 
                  onRegionClick={(comunidadId, comunidadName) => {
                    setComunidadFilter(comunidadId);
                    setActiveTab('list');
                    handleSearch();
                  }}
                />
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* By Comunidad */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Clientes por Comunidad Autónoma
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {byComunidad.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm">{item.comunidad_name}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${Math.min(100, (item.count / (byComunidad[0]?.count || 1)) * 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-8 text-right">{item.count}</span>
                        </div>
                      </div>
                    ))}
                    {byComunidad.length === 0 && (
                      <p className="text-gray-500 text-center py-4">Sin datos de ubicación</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* By City */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="w-5 h-5" />
                    Top 10 Ciudades
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {byCity.slice(0, 10).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm">{item.city}</span>
                        <Badge variant="outline">{item.count}</Badge>
                      </div>
                    ))}
                    {byCity.length === 0 && (
                      <p className="text-gray-500 text-center py-4">Sin datos de ciudad</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Top Clients */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Top Clientes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {topClients.map((client, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <div className="font-medium text-sm">{client.name || client.email}</div>
                          <div className="text-xs text-gray-500">{client.city || '-'}</div>
                        </div>
                        <Badge>{client.warranty_count} garantías</Badge>
                      </div>
                    ))}
                    {topClients.length === 0 && (
                      <p className="text-gray-500 text-center py-4">Sin datos</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Device Preferences */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart2 className="w-5 h-5" />
                    Dispositivos Más Reparados
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {devicePrefs.slice(0, 10).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm truncate flex-1 mr-2">{item.device}</span>
                        <Badge variant="secondary">{item.count}</Badge>
                      </div>
                    ))}
                    {devicePrefs.length === 0 && (
                      <p className="text-gray-500 text-center py-4">Sin datos</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Cliente</DialogTitle>
            <DialogDescription>Añadir un nuevo cliente al sistema</DialogDescription>
          </DialogHeader>
          <ClientForm formData={formData} setFormData={setFormData} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
            <Button onClick={handleCreate}>Crear Cliente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
            <DialogDescription>Modificar datos del cliente</DialogDescription>
          </DialogHeader>
          <ClientForm formData={formData} setFormData={setFormData} isEdit />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
            <Button onClick={handleUpdate}>Guardar Cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle del Cliente</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Nombre</Label>
                  <p className="font-medium">{selectedClient.name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Email</Label>
                  <p className="font-medium">{selectedClient.email}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Teléfono</Label>
                  <p className="font-medium">{selectedClient.phone || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Ciudad</Label>
                  <p className="font-medium">{selectedClient.city || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Provincia</Label>
                  <p className="font-medium">{selectedClient.province || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Comunidad</Label>
                  <p className="font-medium">
                    {COMUNIDADES.find(c => c.id === selectedClient.comunidad)?.name || selectedClient.comunidad || '-'}
                  </p>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500">Dirección completa</Label>
                <p className="font-medium">
                  {[selectedClient.street_name, selectedClient.street_number, selectedClient.postal_code, selectedClient.city].filter(Boolean).join(', ') || '-'}
                </p>
              </div>

              {selectedClient.notes && (
                <div>
                  <Label className="text-gray-500">Notas</Label>
                  <p>{selectedClient.notes}</p>
                </div>
              )}

              {/* Warranties */}
              <div>
                <Label className="text-gray-500 mb-2 block">Garantías ({selectedClient.warranty_count || 0})</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedClient.warranties?.slice(0, 5).map((w, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                      <span>{w.product_name}</span>
                      <Badge variant="outline">{w.status}</Badge>
                    </div>
                  ))}
                  {(!selectedClient.warranties || selectedClient.warranties.length === 0) && (
                    <p className="text-gray-500 text-sm">Sin garantías</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar Clientes</DialogTitle>
            <DialogDescription>Sube un archivo CSV con los datos de clientes</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-2">Arrastra un archivo CSV o haz clic para seleccionar</p>
              <input
                type="file"
                accept=".csv"
                onChange={handleImport}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload">
                <Button variant="outline" as="span" className="cursor-pointer">
                  Seleccionar archivo
                </Button>
              </label>
            </div>
            
            <Button variant="link" onClick={downloadTemplate} className="w-full">
              <Download className="w-4 h-4 mr-2" />
              Descargar plantilla CSV
            </Button>

            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.imported > 0 ? 'bg-green-50' : 'bg-yellow-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {importResult.imported > 0 ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                  )}
                  <span className="font-medium">
                    {importResult.imported} importados, {importResult.skipped} omitidos
                  </span>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="text-sm text-red-600 mt-2">
                    {importResult.errors.slice(0, 5).map((err, idx) => (
                      <p key={idx}>{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Client Form Component
function ClientForm({ formData, setFormData, isEdit = false }) {
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Email *</Label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          disabled={isEdit}
          required
        />
      </div>
      <div>
        <Label>Nombre</Label>
        <Input
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
        />
      </div>
      <div>
        <Label>Teléfono</Label>
        <Input
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
        />
      </div>
      <div>
        <Label>Código Postal</Label>
        <Input
          value={formData.postal_code}
          onChange={(e) => handleChange('postal_code', e.target.value)}
        />
      </div>
      <div>
        <Label>Calle</Label>
        <Input
          value={formData.street_name}
          onChange={(e) => handleChange('street_name', e.target.value)}
        />
      </div>
      <div>
        <Label>Número</Label>
        <Input
          value={formData.street_number}
          onChange={(e) => handleChange('street_number', e.target.value)}
        />
      </div>
      <div>
        <Label>Ciudad</Label>
        <Input
          value={formData.city}
          onChange={(e) => handleChange('city', e.target.value)}
        />
      </div>
      <div>
        <Label>Provincia</Label>
        <Input
          value={formData.province}
          onChange={(e) => handleChange('province', e.target.value)}
        />
      </div>
      <div>
        <Label>Comunidad Autónoma</Label>
        <Select value={formData.comunidad || "none"} onValueChange={(v) => handleChange('comunidad', v === "none" ? "" : v)}>
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Seleccionar...</SelectItem>
            {COMUNIDADES.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>País</Label>
        <Input
          value={formData.country}
          onChange={(e) => handleChange('country', e.target.value)}
        />
      </div>
      <div className="col-span-2">
        <Label>Notas</Label>
        <Input
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Notas internas sobre el cliente"
        />
      </div>
      <div className="col-span-2">
        <Label>Tags (separados por coma)</Label>
        <Input
          value={formData.tags}
          onChange={(e) => handleChange('tags', e.target.value)}
          placeholder="vip, gamer, empresas"
        />
      </div>
    </div>
  );
}

export default AdminClients;
